import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import { Button, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { generateClient } from "aws-amplify/api";
import { listAgreements, listContents } from "../../graphql/queries";
import { deleteContent } from "../../graphql/mutations";
import { useNavigate } from "react-router-dom";
import { Delete, Edit } from "@mui/icons-material";
import Label from "../../components/Label";
import CustomDataGrid from "../../components/CustomDataGrid";
import Box from "@mui/material/Box";
import moment from "moment";
import ActionsPopover from "../../components/actionsPopover";import { toast } from "react-toastify";
import { deleteAgreement } from "../../graphql/mutations";
import { current } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import { setAgreementDetails } from "../../redux/slices/agreementFormSlice";
import { handleCompleteStep } from "../../redux/slices/addNewContentStepsSlice";
const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  dataGridContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
  dataGrid: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      "& .MuiDataGrid-columnHeaders": {
        display: "none",
      },
      "& .MuiDataGrid-cell": {
        display: "block",
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1, 2),
        "&:not(:last-child)": {
          borderBottom: 0,
        },
        "&:before": {
          content: "attr(data-field)",
          fontWeight: "bold",
          marginBottom: theme.spacing(1),
        },
      },
    },
  },
}));

const Cycles = () => {
  const navigate = useNavigate();
  const classes = useStyles();
  const client = generateClient();
  const [contentList, setContentList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const dispatch = useDispatch();
  useEffect(() => {
    fetchCategory();
  }, [deleteSuccess]);

  const calculateCurrentCycle = (
    numberOfMonthsPerCycle,
    dateOfAgreement,
    refreshCycles
  ) => {
    let currentCycle = 1; // Default to the first cycle

    const daysPerCycle = numberOfMonthsPerCycle * 30; // Convert months to days
    const agreementDate = new Date(dateOfAgreement); // Date of the initial agreement

    // Ensure all necessary parameters are present and valid
    if (!daysPerCycle || !agreementDate || !refreshCycles) {
      console.error("Invalid input parameters");
      return currentCycle; // Return default if parameters are invalid
    }

    // Calculate the number of days since the date of agreement until now
    const currentDate = new Date();

    if (agreementDate > currentDate) {
      return currentCycle;
    }
    // Current date
    const timeDifference = currentDate - agreementDate; // Time difference in milliseconds
    const totalDaysSinceAgreement = Math.floor(
      timeDifference / (1000 * 60 * 60 * 24)
    ); // Convert to days

    // Determine the number of full cycles that have passed
    const cyclesPassed = Math.floor(totalDaysSinceAgreement / daysPerCycle) + 1; // Add 1 to start counting from cycle 1

    // Calculate the current cycle based on the passed cycles and refresh cycles limit
    currentCycle = cyclesPassed; // Ensure it doesn't exceed the refresh cycles

    // Log for debugging
    console.log(cyclesPassed, "cyclesPassed");
    console.log(currentCycle, "currentCycle");

    return currentCycle; // Return the calculated current cycle
  };

  const calculateDaysRemainingInCC = (
    dateOfAgreement,
    numberOfMonthsPerCycle,
    daysOfAgreement
  ) => {
    const agreementDate = new Date(dateOfAgreement);
    const daysPerCycle = numberOfMonthsPerCycle * 30;

    const expiryDate = new Date(
      agreementDate.getTime() + daysOfAgreement * 24 * 60 * 60 * 1000
    );
    const totalDaysRemaining = Math.floor(
      (new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24)
    );
    const remainingCycles = totalDaysRemaining / daysPerCycle;
    const wholeNumber = Math.floor(remainingCycles);
    const decimalNumber = remainingCycles - wholeNumber;
    const cyclesDaysRemaining = Math.floor(decimalNumber * daysPerCycle);

    return cyclesDaysRemaining;
  };

  const availableCycle = (
    dateOfAgreement,
    numberOfMonthsPerCycle,
    daysOfAgreement,
    numberOfRefreshCycles
  ) => {
    const currentCycle = calculateCurrentCycle(
      numberOfMonthsPerCycle,
      dateOfAgreement,
      numberOfRefreshCycles
    );

    const daysRemainingInCurrentCycle = calculateDaysRemainingInCC(
      dateOfAgreement,
      numberOfMonthsPerCycle,
      daysOfAgreement
    );

    if (
      daysRemainingInCurrentCycle <= 14 &&
      numberOfRefreshCycles > currentCycle
    ) {
      return currentCycle + 1;
    } else {
      return currentCycle;
    }
  };

  const deleteAgreementById = async (id) => {
    try {
      const response = await client.graphql({
        query: deleteAgreement,
        variables: {
          input: {
            id: id,
          },
        },
      });
      if (response.data.deleteAgreement) {
        setDeleteSuccess(!deleteSuccess);
        toast.success("Agreement Has Been Deleted!");
      }
    } catch (error) {
      toast.error(error.message);
      console.log(error.message);
    }
  };

  const fetchCategory = async () => {
    try {
      setLoading(true);
      const response = await client.graphql({
        query: listAgreements,
      });
      const contentList = response.data.listAgreements.items;
      setContentList(contentList);
    } catch (error) {
      console.error("Error fetching category:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "dateOfAgreement",
      headerName: "Date Of Agreement",
      flex: 1,
      renderCell: (params) => (
        <div>{moment(params?.value).format("DD MMM YYYY")}</div>
      ),
    },
    {
      field: "agreementFilePath",
      headerName: "Agreement",
      flex: 1,
      renderCell: (params) => <div>{params.value?.split("/").pop()}</div>,
    },
    {
      field: "numberOfRefreshCycles",
      headerName: "Refresh Cycles",
    },
    {
      field: "numberOfMonthsPerCycle",
      headerName: "Months/Cycle",
    },

    {
      field: "days",
      headerName: "Days Remaining In Current Cycle",
      flex: 1,
      valueGetter: (params) =>
        calculateDaysRemainingInCC(
          params?.row?.dateOfAgreement,
          params?.row?.numberOfMonthsPerCycle,
          params?.row?.daysOfAgreement
        ),
    },
    {
      field: "current_cycle",
      headerName: "Current Cycle",
      flex: 1,
      valueGetter: (params) =>
        calculateCurrentCycle(
          params?.row?.numberOfMonthsPerCycle,
          params?.row?.dateOfAgreement,
          params?.row?.numberOfRefreshCycles
        ),
    },
    {
      field: "availableCycle",
      headerName: "Next Cycle",
      flex: 1,
      valueGetter: (params) =>
        availableCycle(
          params?.row?.dateOfAgreement,
          params?.row?.numberOfMonthsPerCycle,
          params?.row?.daysOfAgreement,
          params?.row?.numberOfRefreshCycles
        ),
    },
    {
      field: "addContent",
      headerName: "Add Content To Existing Cycle",
      flex: 1,
      renderCell: (params) => (
        <Button
          onClick={() => {
            const currentCycle = calculateCurrentCycle(
              params?.row?.numberOfMonthsPerCycle,
              params?.row?.dateOfAgreement,
              params?.row?.numberOfRefreshCycles
            );
            const cycleAvailable = availableCycle(
              params?.row?.dateOfAgreement,
              params?.row?.numberOfMonthsPerCycle,
              params?.row?.daysOfAgreement,
              params?.row?.numberOfRefreshCycles
            );
            dispatch(setAgreementDetails(params?.row));
            dispatch(handleCompleteStep(0));

            navigate(
              `/agreement/${params?.row?.id}/cycle/${currentCycle}/${cycleAvailable}`
            );
          }}
          sx={{ textTransform: "capitalize" }}
        >
          Add Content
        </Button>
      ),
    },
  ];

  return (
    <>
      <Typography component={"h2"} variant="h5">
        Content Cycles
      </Typography>
      <Box marginTop={2}>
        <div className={classes.dataGridContainer}>
          <CustomDataGrid
            loading={loading}
            rows={contentList}
            columns={columns}
            pageSize={10}
          />
        </div>
      </Box>
    </>
  );
};

export default Cycles;
